import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import api from '../../../services/api';
import { numericFilterColumn } from '../../../utils/numericFilterColumn';
import { dateFilterColumn } from '../../../utils/dateFilterColumn';
import { OWNER_ROLES } from '../../preboarding-tasks/config';
import { positionAsync } from '../../positions/positionPicker';

// ObTask.Period'in kabul ettiği üç kova (bkz. Models/ObTask.cs) — 30/60/90
// dönem onaylarının checklist'i tam olarak bu değerle süzülür.
export const OB_TASK_PERIODS = ['day_1_30', 'day_31_60', 'day_61_90'];

const boolFilter = (label, t) => ({
  filter: true, sort: false, filterType: 'custom',
  filterOptions: {
    defaultOp: 'eq',
    display: (filterList, onChange, index, column) => {
      const current = filterList[index] && filterList[index][0] !== undefined ? filterList[index][0] : '';
      return (
        <FormControl variant="standard" fullWidth>
          <InputLabel>{label}</InputLabel>
          <Select
            value={current}
            onChange={(e) => {
              const v = e.target.value;
              filterList[index] = v === '' ? [] : [v];
              onChange(filterList[index], index, column);
            }}
          >
            <MenuItem value=""><em>{t('common.all')}</em></MenuItem>
            <MenuItem value="true">{t('common.yes')}</MenuItem>
            <MenuItem value="false">{t('common.no')}</MenuItem>
          </Select>
        </FormControl>
      );
    },
  },
});

const useObTaskTemplatesApi = (queryParams) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const [fetching, setFetching] = useState(true);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageSize: 10, pageIndex: 0 });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const lastValidQueryParams = useRef(null);

  useEffect(() => {
    const paramsToUse = queryParams && Object.keys(queryParams).length > 0
      ? queryParams
      : lastValidQueryParams.current || { pageNumber: 1, pageSize: 10 };
    lastValidQueryParams.current = paramsToUse;
    setFetching(true);
    api.get(`/v1/onboarding-task-templates?${new URLSearchParams(paramsToUse)}`)
      .then((res) => {
        const result = res.data?.data;
        const items = Array.isArray(result) ? result : (result?.data || []);
        setData(items);
        setPagination({
          totalRowCount: result?.totalRowCount || items.length,
          pageIndex: result?.pageIndex ? result.pageIndex - 1 : 0,
          pageSize: result?.pageSize || items.length || 10,
        });
      })
      .finally(() => setFetching(false));
  }, [queryParams, refreshTrigger]);

  const refresh = useCallback(() => setRefreshTrigger((p) => p + 1), []);

  // Form value (parsed items array) ↔ API value (ItemsJson string). Items with
  // an empty title are silently dropped — the editor always appends one blank
  // row template users may leave unfilled.
  const toPayload = useCallback((formData) => ({
    name: formData.name,
    departmentId: formData.departmentId || null,
    positionId: formData.positionId || null,
    isDefault: !!formData.isDefault,
    itemsJson: JSON.stringify(
      (formData.items || [])
        .filter((it) => (it.title || '').trim() !== '')
        // OBF-03 — GÖNDERİM sözleşmesi de PB'ninkiydi: `period` hiç yazılmıyor,
        // onay bayrağı OB'nin OKUMADIĞI adla (`requiresHrApproval`) gidiyordu.
        // Backend'in gerçekten okuduğu adlar: ObOnboardingDataAccess
        // .BuildTasksFromTemplate → "period" ve "requiresManagerApproval".
        .map((it) => ({
          key: it.key,
          title: it.title.trim(),
          ownerRole: it.ownerRole || 'hr',
          slaHours: Number(it.slaHours) || 0,
          period: OB_TASK_PERIODS.includes(it.period) ? it.period : 'day_1_30',
          isMandatory: !!it.isMandatory,
          requiresManagerApproval: !!it.requiresManagerApproval,
          dependsOn: it.dependsOn || [],
        })),
    ),
  }), []);

  const toFormValues = useCallback((row) => {
    let items = [];
    try {
      const parsed = JSON.parse(row.itemsJson || '[]');
      if (Array.isArray(parsed)) {
        // OBF-03 — eşleyici de PB sözleşmesini kopyalamıştı: `period` düşüyor,
        // `requiresManagerApproval` hiç okunmuyordu. Yani mevcut bir şablonu
        // AÇIP KAYDETMEK bile bu iki alanı siliyordu. Backend'in OKUDUĞU adlar
        // yazılır; eski şablonlarda period yoksa materializer'ın varsayılanı
        // (`day_1_30`) AÇIKÇA gösterilir.
        items = parsed.map((it, i) => ({
          key: it.key || `task-${i + 1}`,
          title: it.title || '',
          ownerRole: it.ownerRole || 'hr',
          slaHours: it.slaHours ?? 0,
          period: OB_TASK_PERIODS.includes(it.period) ? it.period : 'day_1_30',
          isMandatory: !!it.isMandatory,
          requiresManagerApproval: !!it.requiresManagerApproval,
          dependsOn: Array.isArray(it.dependsOn) ? it.dependsOn : [],
        }));
      }
    } catch {
      items = [];
    }
    return {
      id: row.id,
      name: row.name,
      departmentId: row.departmentId || null,
      positionId: row.positionId || null,
      isDefault: !!row.isDefault,
      items,
    };
  }, []);

  const create = useCallback(async (formData) => {
    try { await api.post('/v1/onboarding-task-templates', toPayload(formData)); refresh(); return true; }
    catch { return false; }
  }, [refresh, toPayload]);

  const update = useCallback(async (id, formData) => {
    try { await api.put(`/v1/onboarding-task-templates/${id}`, toPayload(formData)); refresh(); return true; }
    catch { return false; }
  }, [refresh, toPayload]);

  const remove = useCallback(async (id) => {
    try { await api.delete(`/v1/onboarding-task-templates/${id}`); refresh(); return true; }
    catch { return false; }
  }, [refresh]);

  const columns = useMemo(() => [
    { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
    {
      name: 'name',
      label: t('onboardingTaskTemplate.name'),
      options: {
        filter: true, sort: true,
        filterType: 'textField',
        filterOptions: { defaultOp: 'like' },
      },
    },
    {
      name: 'isDefault',
      label: t('onboardingTaskTemplate.isDefault'),
      options: { ...boolFilter(t('onboardingTaskTemplate.isDefault'), t), customBodyRender: (v) => v ? '✓' : '-' },
    },
    numericFilterColumn('itemCount', t('onboardingTaskTemplate.itemCount'), { decimals: 0 }),
    dateFilterColumn('createDate', t('onboardingTaskTemplate.createDate'), lang),
  ], [t, lang]);

  const objectSchema = useMemo(() => ([
    { name: 'name', label: t('onboardingTaskTemplate.form.name'), type: 'text', required: true },
    {
      name: 'departmentId', label: t('onboardingTaskTemplate.form.department'), type: 'asyncSelect', grid: 6,
      description: t('onboardingTaskTemplate.form.scopeHint'),
      onSearch: async (q) => {
        const params = q ? { 'name[like]': q, pageSize: 20 } : { pageSize: 20 };
        const res = await api.get('/v1/departments', { params });
        const r = res.data?.data; const items = Array.isArray(r) ? r : (r?.data || []);
        return items.map((d) => ({ id: d.id, name: d.name }));
      },
      onGetById: async (id) => {
        const res = await api.get(`/v1/departments/${id}`);
        const d = res.data?.data || {};
        return { id, name: d.name || '' };
      },
    },
    {
      name: 'positionId', label: t('onboardingTaskTemplate.form.position'), type: 'asyncSelect', grid: 6,
      // Pozisyonun `name` alanı YOKTUR ve filtre `name[like]` tanımaz — eski yerel seçici
      // BOŞ etiketli satırlar çiziyor, yazılanı yok sayıyordu (İşe Hazırlık tarafında
      // 2026-09-25'te kapanan kusurun ikizi). Ortak zengin seçici kullanılır.
      ...positionAsync,
      searchPlaceholder: t('position.pickerSearchPlaceholder'),
    },
    {
      name: 'isDefault', label: t('onboardingTaskTemplate.form.isDefault'), type: 'checkbox',
      description: t('onboardingTaskTemplate.form.isDefaultHint'),
    },
    {
      name: 'items', label: t('onboardingTaskTemplate.form.items'), type: 'taskTemplateItems',
      ownerRoles: OWNER_ROLES,
      // OBF-03 — işe alıştırma materializer'ı `period` ve `requiresManagerApproval`
      // okur; düzenleyici PB sözleşmesiyle (period yok, requiresHrApproval)
      // çalıştığı sürece her görev 1-30 güne düşüyor ve yönetici onayı dalı hiç
      // açılamıyordu. İki alanı da BU sayfa bildirir.
      periodOptions: OB_TASK_PERIODS,
      approvalField: 'requiresManagerApproval',
    },
  ]), [t]);

  return { fetching, data, pagination, columns, objectSchema, toFormValues, refresh, create, update, remove };
};

export default useObTaskTemplatesApi;
